import React from 'react';
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import endsWith from "underscore.string/endsWith";
import { renderRailsErrors } from '../../utils/rails_error_parser';
import VerticalTabs from "./../layouts/VerticalTabs";
import {
  MainVideoForm,
  ProblemForm,
  SolutionForm,
  ProductForm,
  TractionForm,
  CustomerForm,
  BusinessModelForm,
  MarketForm,
  CompetitionForm,
  VisionForm,
  TeamForm,
  UseOfFundForm,
  RiskAndDisclosureForm,
  Highlights,
  NotableInvestors,
  MediaMentions,
} from './sections';
import {
  BASE_CACHEKEY_MAP,
  usePitch,
  updatePitch,
} from "./queries";
import { useSessionContext } from "../../contexts/SessionContext";
import { CompanyContext } from "../../contexts/CompanyContext";
import { OFFER_STATUSES } from '../../config/constants';

import './Pitch.scss';
import { Loaders } from '..';

function PitchEditor({pitchableType, pitchableId}) {
  const queryClient = useQueryClient();
  const { isPermitted, ACTIONS } = useSessionContext();
  const { selectedCompany } = React.useContext(CompanyContext);
  const {data:pitch={}, isLoading} = usePitch({pitchable_type: pitchableType, pitchable_id: pitchableId })

  for (const [key, value] of Object.entries(pitch)) {
    if(endsWith(key, "_section") && value == null){
      pitch[key] = {}
    }
  }

  const updatePitchMutation = useMutation(
    updatePitch,
    {
      onSuccess: (updatedPitch) => {
        toast.success('Pitch section updated.')
        queryClient.setQueryData(
          [BASE_CACHEKEY_MAP[pitchableType], pitchableId],
          updatedPitch
        )
      },
      onError: (error) => {
        toast.error('Pitch section update failed.')
        renderRailsErrors(error)
      },
    }
  )

  const pitchSections = React.useMemo(
    () => {
      return [
        { tabId: 'video', title: 'Main Video', targetId: 'video-panel', pathName: 'main-video',
          decoratorClass: pitch.main_video_section?.id ? 'complete' : 'incomplete',
          content: <MainVideoForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'highlights', title: 'Highlights', targetId: 'highlights-panel', pathName: 'highlights',
          decoratorClass: pitch.highlights ? 'complete' : 'incomplete',
          content: <Highlights pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId}/>
        },
        { tabId: 'notableinvestors', title: 'Notable Inv.', targetId: 'notableinvestors-panel', pathName: 'notable-investors',
          decoratorClass: pitch.notable_investors ? 'complete' : 'incomplete',
          content: <NotableInvestors pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId}/>
        },
        { tabId: 'mediamentions', title: 'Media', targetId: 'mediamentions-panel', pathName: 'media-mentions',
          decoratorClass: pitch.media_mentions ? 'complete' : 'incomplete',
          content: <MediaMentions pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId}/>
        },
        { tabId: 'problem', title: 'Problem', targetId: 'problem-panel', pathName: 'problem',
          decoratorClass: pitch.problem_section?.id ? 'complete' : 'incomplete',
          content: <ProblemForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'solution', title: 'Solution', targetId: 'solution-panel', pathName: 'solution',
          decoratorClass: pitch.solution_section?.id ? 'complete' : 'incomplete',
          content: <SolutionForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'product', title: 'Product', targetId: 'product-panel', pathName: 'product',
          decoratorClass: pitch.product_section?.id ? 'complete' : 'incomplete',
          content: <ProductForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'traction', title: 'Traction', targetId: 'traction-panel', pathName: 'traction',
          decoratorClass: pitch.traction_section?.id ? 'complete' : 'incomplete',
          content: <TractionForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'customers', title: 'Customers', targetId: 'customers-panel', pathName: 'customers',
          decoratorClass: pitch.customers_section?.id ? 'complete' : 'incomplete',
          content: <CustomerForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'businessmodel', title: 'Business Model', targetId: 'businessmodel-panel', pathName: 'business-model',
          decoratorClass: pitch.business_model_section?.id ? 'complete' : 'incomplete',
          content: <BusinessModelForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'market', title: 'Market', targetId: 'market-panel', pathName: 'market',
          decoratorClass: pitch.market_section?.id ? 'complete' : 'incomplete',
          content: <MarketForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'competition', title: 'Competition', targetId: 'competition-panel', pathName: 'competition',
          decoratorClass: pitch.competition_section?.id ? 'complete' : 'incomplete',
          content: <CompetitionForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'vision', title: 'Vision', targetId: 'vision-panel', pathName: 'vision',
          decoratorClass: pitch.vision_section?.id ? 'complete' : 'incomplete',
          content: <VisionForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'team', title: 'Team', targetId: 'team-panel', pathName: 'team',
          decoratorClass: pitch.team_section?.id ? 'complete' : 'incomplete',
          content: <TeamForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'useoffunds', title: 'Use of Funds', targetId: 'useoffunds-panel', pathName: 'use-of-funds',
          decoratorClass: pitch.use_of_funds_section?.id ? 'complete' : 'incomplete',
          content: <UseOfFundForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
        { tabId: 'risksanddisclosures', title: 'Risks and Disclosures', targetId: 'risksanddisclosures-panel', pathName: 'risks-and-disclosures',
          decoratorClass: pitch.risks_and_disclosures_section?.id ? 'complete' : 'incomplete',
          content: <RiskAndDisclosureForm pitch={pitch} pitchableType={pitchableType} pitchableId={pitchableId} updatePitchMutation={updatePitchMutation} />
        },
      ]
    },
    [pitch]
  );

  if (isLoading)
    return <Loaders.SpacedPageLoader />

  if(pitch.pitchable_status === OFFER_STATUSES.LIVE) {
    return (
      <div>
        Cannot update pitch. Offering is already live.
      </div>
    )
  }

  if(!isPermitted(ACTIONS.WRITE_UPDATE_PITCH, selectedCompany.id)) {
    return (
      <div>
        You are not authorised to edit Pitch.
      </div>
    )
  }

  return (
      <VerticalTabs
        tabs={pitchSections}
        tabIdAccessor={'tabId'}
        targetPanelIdAccessor={'targetId'}
        defaultTabId="video"
      />
  )
}

PitchEditor.propTypes = {
  pitchableType: PropTypes.string.isRequired,
  pitchableId: PropTypes.string.isRequired,
};

export default PitchEditor;
